import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import StatusBadge from '../../components/shared/StatusBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Search, MapPin, Clock, Truck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicTracking = () => {
  const [searchParams] = useSearchParams();
  const [reference, setReference] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReference(ref);
      doSearch(ref);
    }
  }, []);

  const doSearch = async (ref) => {
    if (!ref?.trim()) return;
    setLoading(true);
    setError('');
    setTrackingData(null);
    try {
      const response = await axios.get(`${API_URL}/api/track/${ref.trim()}`);
      setTrackingData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Waybill not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    doSearch(reference);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statusOrder = ['quote_requested', 'in_costing', 'quote_ready', 'accepted', 'dispatch_scheduled', 'collected', 'in_transit', 'at_customs', 'out_for_delivery', 'delivered'];
  
  const getProgress = (status) => {
    const idx = statusOrder.indexOf(status);
    return idx >= 0 ? ((idx + 1) / statusOrder.length) * 100 : 0;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-tracking-page">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center h-16">
            <div className="flex items-center">
              <img src="/cargodash-logo.svg" alt="CargoDash" className="h-9" />
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Hero */}
        <div className="text-center mb-6 sm:mb-10">
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-2 sm:mb-4" style={{ fontFamily: 'Manrope' }}>
            Track Your Shipment
          </h1>
          <p className="text-sm sm:text-lg text-slate-600">
            Enter your waybill reference number to track your shipment in real-time
          </p>
        </div>

        {/* Search Form */}
        <Card className="card-elevated mb-6 sm:mb-8">
          <CardContent className="p-4 sm:p-6">
            <form onSubmit={handleSearch} className="flex gap-2 sm:gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                <Input
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Enter waybill number"
                  className="pl-9 sm:pl-12 h-11 sm:h-14 text-sm sm:text-lg"
                  data-testid="public-tracking-input"
                />
              </div>
              <Button type="submit" disabled={loading} className="btn-primary h-11 sm:h-14 px-5 sm:px-8 text-sm sm:text-lg" data-testid="public-tracking-btn">
                {loading ? '...' : 'Track'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-8 text-center text-red-700">
            {error}
          </div>
        )}

        {/* Results */}
        {trackingData && (
          <div className="space-y-6 animate-fade-in" data-testid="public-tracking-results">
            {/* Status Overview */}
            <Card className="card-elevated">
              <CardContent className="p-5 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">Reference Number</p>
                    <p className="text-2xl sm:text-3xl font-bold text-slate-900">{trackingData.reference_number}</p>
                    <p className="text-xs sm:text-sm text-slate-600 mt-0.5">{trackingData.client_name}</p>
                  </div>
                  <StatusBadge status={trackingData.status} className="text-sm sm:text-base px-3 sm:px-4 py-1.5 sm:py-2 self-start sm:self-auto" />
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="h-2 sm:h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-teal-600 transition-all duration-500"
                      style={{ width: `${getProgress(trackingData.status)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Route */}
                <div className="flex items-center justify-between py-4 sm:py-6 border-t border-slate-100 gap-2">
                  <div className="flex items-center space-x-2 sm:space-x-4">
                    <div className="h-9 w-9 sm:h-12 sm:w-12 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-4 w-4 sm:h-6 sm:w-6 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-slate-500">Origin</p>
                      <p className="text-base sm:text-xl font-semibold text-slate-900">{trackingData.origin_city}</p>
                    </div>
                  </div>
                  <div className="flex-1 mx-2 sm:mx-6">
                    <div className="border-t-2 border-dashed border-slate-200 relative">
                      <Truck className="absolute left-1/2 -translate-x-1/2 -top-3 h-5 w-5 sm:h-6 sm:w-6 text-teal-600 bg-white sm:bg-slate-50 px-0.5" />
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs sm:text-sm text-slate-500">Destination</p>
                    <p className="text-base sm:text-xl font-semibold text-slate-900">{trackingData.destination_city}</p>
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 pt-4 sm:pt-6 border-t border-slate-100">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">Sender</p>
                    <p className="text-sm sm:text-base font-medium text-slate-900">{trackingData.sender_company}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">Receiver</p>
                    <p className="text-sm sm:text-base font-medium text-slate-900">{trackingData.receiver_company}</p>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <p className="text-xs sm:text-sm text-slate-500">Est. Delivery</p>
                    <p className="text-sm sm:text-base font-medium text-teal-700">{formatDate(trackingData.estimated_delivery_date)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card className="card-elevated">
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-indigo-600" />
                  <CardTitle className="card-title">Shipment Timeline</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {trackingData.tracking_events?.length > 0 ? (
                  <div className="space-y-4">
                    {trackingData.tracking_events.map((event, idx) => (
                      <div key={idx} className="flex">
                        <div className="flex flex-col items-center mr-4">
                          <div className={`w-4 h-4 rounded-full border-2 ${idx === 0 ? 'bg-teal-600 border-teal-600' : 'bg-white border-slate-300'}`}></div>
                          {idx < trackingData.tracking_events.length - 1 && (
                            <div className="w-0.5 h-full bg-slate-200 my-1"></div>
                          )}
                        </div>
                        <div className="pb-6">
                          <div className="flex flex-wrap items-center gap-2">
                            <StatusBadge status={event.status} />
                            <span className="text-sm text-slate-500">{formatDateTime(event.timestamp)}</span>
                          </div>
                          <p className="text-sm font-medium text-slate-900 mt-1">{event.location}</p>
                          {event.notes && <p className="text-sm text-slate-600 mt-1">{event.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-4">No tracking events available</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

      </main>
    </div>
  );
};

export default PublicTracking;
